import { useState } from "react";
import { match } from "ts-pattern";
import { msgid, ngettext, t } from "ttag";

import type { CustomVizPublishedValue } from "metabase/api/ai-streaming/schemas";
import { getErrorMessage } from "metabase/api/utils/errors";
import { ForwardRefLink } from "metabase/common/components/Link";
import { useToast } from "metabase/common/hooks/use-toast";
import { useOpenGitSyncPullRequestMutation } from "metabase/fork/git-sync/api";
import { useCanUseGitSync } from "metabase/fork/git-sync/hooks";
import type { MetabotCustomVizPublishedMessageProps } from "metabase/metabot";
import {
  Anchor,
  Badge,
  Box,
  Button,
  Flex,
  Group,
  Icon,
  Text,
} from "metabase/ui";
import * as Urls from "metabase/urls";

type ExportStatus = "idle" | "starting" | "started";

/**
 * The chat card for a custom visualization Metabot published: what changed for the questions that use it, a link to
 * it, and, where Git sync is set up, a button that exports it to Git as a pull request, so it reaches the instances
 * that import from the repository after a review.
 */
export function CustomVizPublishedMessage({
  value,
  readonly,
}: MetabotCustomVizPublishedMessageProps) {
  const canUseGitSync = useCanUseGitSync();
  const [openPullRequest] = useOpenGitSyncPullRequestMutation();
  const [sendToast] = useToast();
  const [status, setStatus] = useState<ExportStatus>("idle");

  const exportToGit = async () => {
    setStatus("starting");
    try {
      await openPullRequest({ message: pullRequestTitle(value) }).unwrap();
      setStatus("started");
    } catch (error) {
      setStatus("idle");
      sendToast({
        icon: "warning",
        toastColor: "feedback-negative",
        message: getErrorMessage(
          error,
          t`The pull request couldn't be opened.`,
        ),
      });
    }
  };

  return (
    <Box
      bd="1px solid var(--mb-color-border-neutral)"
      bdrs="sm"
      p="md"
      data-testid="metabot-custom-viz-published"
    >
      <Flex align="center" gap="sm">
        <Icon name="palette" c="core-brand" />
        <Text fw="bold" truncate>
          {value.name}
        </Text>
        {value.version && <Badge variant="light">{value.version}</Badge>}
      </Flex>
      <Text c="text-secondary" mt="xs">
        <ChangeText value={value} />
      </Text>
      {status === "started" && (
        <Text mt="xs">
          {t`Opening a pull request with everything Git sync exports that changed here. The Git sync panel shows when it's open.`}
        </Text>
      )}
      <Group gap="sm" mt="md">
        <Anchor
          component={ForwardRefLink}
          to={Urls.customVizEdit(value.id)}
          fw="bold"
        >
          {t`Open visualization`}
        </Anchor>
        {canUseGitSync && !readonly && status !== "started" && (
          <Button
            size="compact-sm"
            variant="subtle"
            leftSection={<Icon name="git_branch" />}
            loading={status === "starting"}
            onClick={exportToGit}
          >
            {t`Export to Git as a pull request`}
          </Button>
        )}
      </Group>
    </Box>
  );
}

function pullRequestTitle({ name, version }: CustomVizPublishedValue) {
  return version
    ? t`Publish the custom visualization ${name} ${version}`
    : t`Publish the custom visualization ${name}`;
}

function ChangeText({ value }: { value: CustomVizPublishedValue }) {
  const { uses } = value;
  const usedBy = ngettext(
    msgid`${uses} question or dashboard card uses it.`,
    `${uses} questions and dashboard cards use it.`,
    uses,
  );
  return match(value.kind)
    .with(
      "initial",
      () => t`Published. Pick it in the visualization picker of any question.`,
    )
    .with("compatible", () => `${t`Published a compatible version.`} ${usedBy}`)
    .with(
      "behavior-change",
      () =>
        `${t`Published a version that looks different in places.`} ${usedBy}`,
    )
    .with(
      "breaking",
      () =>
        `${t`Published a version whose settings changed; Metabot can move the questions that use it.`} ${usedBy}`,
    )
    .exhaustive();
}
